import fetch from 'node-fetch';
import router from '@/router';
import store from '@/store';

const { Buffer } = require('buffer/');

function generateState (returnUrl) {
  const state = { returnUrl: returnUrl };

  return Buffer.from(JSON.stringify(state), 'utf-8').toString('base64');
}

async function makeApiRequest (path, method = 'GET', body = {}, impersonateToken = store.state.impersonateData) {
  const route = router.currentRoute.value;

  const params = {
    method: method,
    headers: { 'content-Type': 'application/json' },
    body: Object.keys(body).length !== 0 ? JSON.stringify(body) : undefined
  };

  let url = path;

  if (store.state.agency) {
    // check if the url already contains params
    url = url.includes('?') ? `${url}&` : `${url}?`;

    url = `${url}agency=${encodeURI(store.state.agency)}`;
  }

  if (impersonateToken) {
    // check if the url already contains params
    url = url.includes('?') ? `${url}&` : `${url}?`;

    url = `${url}impersonateData=${encodeURI(impersonateToken)}`;
  }

  return await fetch(`/espacepersonnel${url}`, params)
    .then(response => response.json())
    .then(response => {
      if (response.loginUrl) {
        // TODO IMPROVE
        let return_url = '/espacepersonnel';

        // vue app runs in /reservation and vue thinks it's /, /?
        return_url += route.path === '/' ? route.fullPath.substring(1) : route.fullPath;

        // this.$cookies.set('return_url', return_url);

        store.commit('setLoading', true);

        window.location.href = response.loginUrl;
        throw (response);
      }

      return response;
    }).catch(err => {
      console.log('[ERROR]', err);
      return err;
    });
}

async function getUser () {
  const user = await makeApiRequest('/api/user', 'GET');
  if (!user.success) {
    return null; // Loading and redirect to SSO
  }

  // Remove self user from contact
  const contactsData = user.data.contacts.filter(c => c.uuid !== user.data.uuid);
  store.commit('setUser', user.data);
  store.commit('setContacts', contactsData);

  return user;
}

async function getOrders (ids, start = 0, limit = 10, status = null) {
  const orders = await makeApiRequest(`/api/orders?id=${ids.join(",")}&start=${start}&limit=${limit}&status=${status}`, 'GET');
  if (!orders.success) {
    return {
      count: 0,
      items: []
    };
  }
  return orders.data;
}

async function getCodes (start = 0, limit = 10) {
  const codes = await makeApiRequest(`/api/codes?start=${start}&limit=${limit}`, 'GET');
  if (!codes.success) {
    return {
      count: 0,
      items: []
    };
  }
  return codes.data;
}

async function getReferrals (start = 0, limit = 10) {
  const codes = await makeApiRequest(`/api/referrals?start=${start}&limit=${limit}`, 'GET');
  if (!codes.success) {
    return {
      count: 0,
      items: []
    };
  }
  return codes.data;
}

async function getOptions (start = 0, limit = 10) {
  const orders = await makeApiRequest(`/api/options?start=${start}&limit=${limit}`, 'GET');
  if (!orders.success) {
    return {
      count: 0,
      items: []
    };
  }
  return orders.data;
}

async function cancelOption (bookingNumber) {
  const response = await makeApiRequest(`/api/option/cancel?number=${bookingNumber}`, 'GET');
  return response;
}

async function getOrder (bookingNumber) {
  const order = await makeApiRequest(`/api/booking-data?bookingNumber=${bookingNumber}`, 'GET');

  if (!order.success) {
    return {};
  }

  return order.data;
}
async function getBillets (start = 0, limit = 10) {
  const orders = await makeApiRequest(`/api/billets?start=${start}&limit=${limit}`, 'GET');
  if (!orders.success) {
    return {};
  }

  return orders.data;
}

async function updateUser (data) {
  const user = await makeApiRequest('/api/profile', 'PUT', data);
  if (!user.success) {
    return null; // Loading and redirect to SSO
  }

  store.commit('setUser', user.data);

  return user;
}

async function createContact (data, parent_uuid = undefined) {
  const user = await makeApiRequest('/api/contact', 'POST', { ...data, parent_uuid });
  if (!user.success && user.code !== 'ALREADY_EXISTS') {
    return null; // Loading and redirect to SSO
  }

  return user;
}

async function updateContact (data) {
  const user = await makeApiRequest('/api/contact', 'PUT', data);
  if (!user.success) {
    return null; // Loading and redirect to SSO
  }

  return user;
}

async function updateNewsletters (data) {
  const user = await makeApiRequest('/api/newsletters', 'PUT', data);
  if (!user.success) {
    return null; // Loading and redirect to SSO
  }

  return user;
}

async function getCenters (start = 0, limit = 1000) {
  const centers = await makeApiRequest(`/api/centers?start=${start}&limit=${limit}`, 'GET');
  if (!centers.success) {
    return {};
  }

  return centers.data;
}

async function createWallet (data) {
  const wallet = await makeApiRequest('/api/wallet', 'POST', { ...data });
  if (!wallet.success) {
    return null; // Loading and redirect to SSO
  }

  return wallet;
}

async function getWallets (start = 0, limit = 11) {
  const wallets = await makeApiRequest(`/api/wallets?start=${start}&limit=${limit}`, 'GET');
  if (!wallets.success) {
    return {};
  }

  return wallets.data;
}

async function getWalletsPaymentHistory (wallet_uuid, startDate = null, endDate = null) {
  let url = `/api/wallet-payment-history?uuid=${wallet_uuid}`;

  if (startDate) {
    url += `&startdate=${startDate}${endDate ? `&enddate=${endDate}` : ''}`;
  }

  const walletHistory = await makeApiRequest(url, 'GET');
  if (!walletHistory.success) {
    return {};
  }

  return walletHistory.data;
}

async function shareQRcode (email, wallet_uuid, locale) {
  await makeApiRequest(`/api/share-qrcode?email=${email}&wallet_uuid=${wallet_uuid}&locale=${locale}`, 'GET');
}

async function getPayboxForm (data) {
  const paybox = await makeApiRequest('/api/payment', 'POST', { ...data });

  if (!paybox.success) {
    return {};
  }

  return paybox;
}

async function walletRefund (data) {
  return await makeApiRequest('/api/refund', 'POST', { ...data });
}

async function getSiteConfiguration () {
  const siteConfiguration = await makeApiRequest('/api/siteconfiguration');

  if (!siteConfiguration.success) {
    return {};
  }

  store.commit('setSiteConfiguration', siteConfiguration);

  return siteConfiguration;
}

async function createReferral (data, user_uuid = undefined) {
  const referral = await makeApiRequest('/api/referral', 'POST', { ...data, user_uuid });

  return referral;
}

async function isImpersonateMode (impersonateToken) {
  return makeApiRequest('/api/impersonateMode', 'GET', {}, impersonateToken);
}

async function addVisibleOrder (booking, customer_uuid, clientReference) {
  return makeApiRequest('/api/addVisibleOrder', 'POST', { booking: booking, customer_uuid: customer_uuid, clientReference: clientReference });
}

export {
  makeApiRequest,
  getUser,
  getOrder,
  getOrders,
  getCodes,
  getOptions,
  cancelOption,
  getBillets,
  updateUser,
  createContact,
  updateContact,
  updateNewsletters,
  createWallet,
  getCenters,
  getWallets,
  getWalletsPaymentHistory,
  shareQRcode,
  walletRefund,
  getPayboxForm,
  getSiteConfiguration,
  createReferral,
  getReferrals,
  isImpersonateMode,
  addVisibleOrder
};